import React, { useEffect, useRef, useState } from 'react';
import axiosInstance, { API_BASE_URL } from '../../utils/axiosConfig';
import { toast } from 'react-toastify';
import IkonSecici from '../IkonSecici';
import GaleriYonetici, { GaleriOgesi } from './GaleriYonetici';
import MaddeYonetici, { MaddeOgesi } from './MaddeYonetici';

interface ProjeOzellik {
    projeOzellikId: number;
    ozellik: string;
    deger: string;
    ikon?: string | null;
    aktif: boolean;
}

interface BolumGorsel {
    projeDaireTipiBolumGorselID: number;
    gorselYolu: string;
    sira: number;
}

interface DaireTipiBolum {
    projeDaireTipiBolumID: number;
    gorseller: BolumGorsel[];
    bolumAdi: string;
    aciklama?: string | null;
    ikon?: string | null;
    sira: number;
}

interface DaireTipiSatir {
    daireTipID: number;
    tip: string;
    projeDaireTipiID: number | null;
    ikon: string | null;
    bolumler: DaireTipiBolum[];
}

interface Video {
    projeVideoID: number;
    youtubeLink: string;
    baslik?: string | null;
    sira: number;
}

interface ProjeSayfaVeri {
    projeID: number;
    projeAdi: string;
    projeLogo: string | null;
    hakkindaMetni: string | null;
    kapakGorselleri: GaleriOgesi[];
    vaziyetPlaniGorselleri: GaleriOgesi[];
    videolar: Video[];
    ozellikler: ProjeOzellik[];
    daireTipleri: DaireTipiSatir[];
    disMekan: { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[] };
    konumCevre: { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[] };
    icMekan: { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[] };
    sosyalAlan: { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[] };
}

type BolumKey = 'hakkinda' | 'kapak' | 'ozellikler' | 'daireTipleri' | 'vaziyet' | 'video' | 'disMekan' | 'konumCevre' | 'icMekan' | 'sosyalAlan';

const BOLUMLER: { key: BolumKey; ad: string; ikon: string }[] = [
    { key: 'hakkinda', ad: 'Proje Hakkında', ikon: 'fas fa-align-left' },
    { key: 'kapak', ad: 'Kapak Manşet', ikon: 'fas fa-images' },
    { key: 'ozellikler', ad: 'Proje Özellikleri', ikon: 'fas fa-list-check' },
    { key: 'daireTipleri', ad: 'Daire Tipleri', ikon: 'fas fa-door-open' },
    { key: 'vaziyet', ad: 'Vaziyet Planı', ikon: 'fas fa-map' },
    { key: 'video', ad: 'Video', ikon: 'fab fa-youtube' },
    { key: 'disMekan', ad: 'Dış Mekan', ikon: 'fas fa-tree' },
    { key: 'konumCevre', ad: 'Konum / Çevre', ikon: 'fas fa-map-marker-alt' },
    { key: 'icMekan', ad: 'İç Mekan', ikon: 'fas fa-couch' },
    { key: 'sosyalAlan', ad: 'Sosyal Alanlar', ikon: 'fas fa-users' },
];

interface Props {
    projeID: number;
    onClose: () => void;
}

const ProjeSayfaYonetim: React.FC<Props> = ({ projeID, onClose }) => {
    const [veri, setVeri] = useState<ProjeSayfaVeri | null>(null);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [aktifBolum, setAktifBolum] = useState<BolumKey>('hakkinda');
    const logoRef = useRef<HTMLInputElement>(null);

    const fetchVeri = async () => {
        try {
            const res = await axiosInstance.get(`/api/ProjeSayfa/${projeID}`);
            setVeri(res.data);
        } catch {
            toast.error('Proje sayfası yüklenirken hata oluştu.');
        } finally {
            setYukleniyor(false);
        }
    };

    useEffect(() => { fetchVeri(); }, [projeID]);

    // ── Proje Hakkında ──────────────────────────────────────────────────
    const [hakkindaMetni, setHakkindaMetni] = useState('');
    const [hakkindaKaydediliyor, setHakkindaKaydediliyor] = useState(false);
    useEffect(() => { if (veri) setHakkindaMetni(veri.hakkindaMetni || ''); }, [veri?.hakkindaMetni]);

    const handleHakkindaKaydet = async () => {
        setHakkindaKaydediliyor(true);
        try {
            await axiosInstance.post(`/api/ProjeSayfa/${projeID}/hakkinda`, { hakkindaMetni });
            toast.success('Kaydedildi.');
        } catch {
            toast.error('Kaydedilirken hata oluştu.');
        } finally {
            setHakkindaKaydediliyor(false);
        }
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !veri) return;
        try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('aciklama', 'Proje Logo');
            const up = await axiosInstance.post('/api/Dosyalar/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            await axiosInstance.post(`/api/ProjeSayfa/${projeID}/logo`, { projeLogo: up.data.dosyaAdi });
            setVeri({ ...veri, projeLogo: up.data.dosyaAdi });
            toast.success('Logo güncellendi.');
        } catch {
            toast.error('Logo yüklenirken hata oluştu.');
        } finally {
            if (logoRef.current) logoRef.current.value = '';
        }
    };

    // ── Proje Özellikleri ───────────────────────────────────────────────
    const [ozellikAd, setOzellikAd] = useState('');
    const [ozellikDeger, setOzellikDeger] = useState('');
    const [ozellikIkon, setOzellikIkon] = useState<string | null>(null);
    const [ozellikEkleniyor, setOzellikEkleniyor] = useState(false);
    const [projeTakipNo, setProjeTakipNo] = useState<string | null>(null);

    useEffect(() => {
        axiosInstance.get(`/api/Projeler/${projeID}`).then(r => setProjeTakipNo(r.data.takipNo)).catch(() => {});
    }, [projeID]);

    const handleOzellikEkle = async () => {
        if (!ozellikAd.trim() || !ozellikDeger.trim() || !projeTakipNo || !veri) return;
        setOzellikEkleniyor(true);
        try {
            const res = await axiosInstance.post('/api/ProjeOzellikler', {
                projeTakipNo, ozellik: ozellikAd.trim(), deger: ozellikDeger.trim(), ikon: ozellikIkon, aktif: true,
            });
            setVeri({ ...veri, ozellikler: [...veri.ozellikler, res.data] });
            setOzellikAd(''); setOzellikDeger(''); setOzellikIkon(null);
        } catch {
            toast.error('Özellik eklenirken hata oluştu.');
        } finally {
            setOzellikEkleniyor(false);
        }
    };

    const handleOzellikSil = async (id: number) => {
        if (!veri) return;
        try {
            await axiosInstance.post(`/api/ProjeOzellikler/delete/${id}`);
            setVeri({ ...veri, ozellikler: veri.ozellikler.filter(o => o.projeOzellikId !== id) });
        } catch {
            toast.error('Silinirken hata oluştu.');
        }
    };

    // ── Daire Tipleri ───────────────────────────────────────────────────
    const [detayTip, setDetayTip] = useState<DaireTipiSatir | null>(null);

    const handleTipIkon = async (tip: DaireTipiSatir, ikon: string | null) => {
        if (!veri) return;
        try {
            const res = await axiosInstance.post('/api/ProjeSayfa/daire-tipi/ikon', { projeID, daireTipID: tip.daireTipID, ikon });
            setVeri({
                ...veri,
                daireTipleri: veri.daireTipleri.map(t => t.daireTipID === tip.daireTipID
                    ? { ...t, ikon, projeDaireTipiID: res.data.projeDaireTipiID }
                    : t),
            });
        } catch {
            toast.error('İkon kaydedilirken hata oluştu.');
        }
    };

    // ── Video ───────────────────────────────────────────────────────────
    const [videoLink, setVideoLink] = useState('');
    const [videoBaslik, setVideoBaslik] = useState('');
    const [videoEkleniyor, setVideoEkleniyor] = useState(false);

    const youtubeEmbed = (link: string): string | null => {
        const m = link.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{6,})/);
        return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    };

    const handleVideoEkle = async () => {
        if (!videoLink.trim() || !veri) return;
        if (!youtubeEmbed(videoLink.trim())) { toast.error('Geçerli bir YouTube linki girin.'); return; }
        setVideoEkleniyor(true);
        try {
            const res = await axiosInstance.post('/api/ProjeSayfa/video', {
                projeID, youtubeLink: videoLink.trim(), baslik: videoBaslik.trim() || null, sira: 0,
            });
            setVeri({ ...veri, videolar: [...veri.videolar, res.data] });
            setVideoLink(''); setVideoBaslik('');
        } catch {
            toast.error('Video eklenirken hata oluştu.');
        } finally {
            setVideoEkleniyor(false);
        }
    };

    const handleVideoSil = async (id: number) => {
        if (!veri) return;
        try {
            await axiosInstance.post(`/api/ProjeSayfa/video/delete/${id}`);
            setVeri({ ...veri, videolar: veri.videolar.filter(v => v.projeVideoID !== id) });
        } catch {
            toast.error('Silinirken hata oluştu.');
        }
    };

    if (yukleniyor || !veri) {
        return (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                <div style={{ background: '#fff', borderRadius: 14, padding: 32, display: 'flex', alignItems: 'center', gap: 10, color: '#64748b' }}>
                    <i className="fas fa-spinner fa-spin"></i> Proje sayfası yükleniyor...
                </div>
            </div>
        );
    }

    const bolumIcerik = () => {
        switch (aktifBolum) {
            case 'hakkinda':
                return (
                    <div>
                        <h3 style={psH3}>Proje Hakkında</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <div style={{ width: 72, height: 72, borderRadius: 12, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                {veri.projeLogo
                                    ? <img src={`${API_BASE_URL}/userFiles/${veri.projeLogo}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    : <i className="fas fa-building" style={{ color: '#94a3b8', fontSize: 24 }}></i>}
                            </div>
                            <div>
                                <input ref={logoRef} type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
                                <button type="button" onClick={() => logoRef.current?.click()} style={psBtnGhost}>
                                    <i className="fas fa-upload" style={{ marginRight: 6 }}></i>Proje Logosu Yükle
                                </button>
                            </div>
                        </div>
                        <textarea
                            value={hakkindaMetni}
                            onChange={e => setHakkindaMetni(e.target.value)}
                            placeholder="Proje hakkında serbest metin..."
                            rows={10}
                            style={{ width: '100%', padding: 14, borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                        <button type="button" onClick={handleHakkindaKaydet} disabled={hakkindaKaydediliyor} style={{ ...psBtnPrimary, marginTop: 12 }}>
                            {hakkindaKaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                );

            case 'kapak':
                return (
                    <div>
                        <h3 style={psH3}>Kapak Manşet</h3>
                        <p style={psAciklama}>Proje kartlarında ve sayfa üstünde gösterilecek görseller.</p>
                        <GaleriYonetici projeID={projeID} kategori="KapakManset" ogeler={veri.kapakGorselleri}
                            onChange={g => setVeri({ ...veri, kapakGorselleri: g })} />
                    </div>
                );

            case 'ozellikler':
                return (
                    <div>
                        <h3 style={psH3}>Proje Özellikleri</h3>
                        <p style={psAciklama}>Bu liste, Proje Detayı &gt; İşlemler altındaki "Proje Özellikleri" ile aynıdır.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                            {veri.ozellikler.map(o => (
                                <div key={o.projeOzellikId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                                    <i className={o.ikon || 'fas fa-check-circle'} style={{ color: '#cd6906', width: 16, textAlign: 'center' }}></i>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{o.ozellik}:</span>
                                    <span style={{ flex: 1, fontSize: 13, color: '#334155' }}>{o.deger}</span>
                                    <button type="button" onClick={() => handleOzellikSil(o.projeOzellikId)} style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', padding: 4 }}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))}
                            {veri.ozellikler.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Henüz özellik eklenmedi.</div>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <IkonSecici value={ozellikIkon} onChange={setOzellikIkon} />
                            <input type="text" placeholder="Özellik (örn: Isıtma)" value={ozellikAd} onChange={e => setOzellikAd(e.target.value)} style={{ ...psInput, flex: 1 }} />
                            <input type="text" placeholder="Değer (örn: Yerden Isıtma)" value={ozellikDeger} onChange={e => setOzellikDeger(e.target.value)} style={{ ...psInput, flex: 1 }} />
                            <button type="button" onClick={handleOzellikEkle} disabled={ozellikEkleniyor} style={psBtnPrimary}>Ekle</button>
                        </div>
                    </div>
                );

            case 'daireTipleri':
                return (
                    <div>
                        <h3 style={psH3}>Daire Tipleri</h3>
                        <p style={psAciklama}>Bu projedeki dairelerde kullanılan tipler. Her tipe ikon ve alt bölümler (görsel + açıklama) eklenebilir.</p>
                        {veri.daireTipleri.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Bu projede henüz daire eklenmemiş.</div>}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                            {veri.daireTipleri.map(tip => (
                                <div key={tip.daireTipID} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, cursor: 'pointer' }}
                                    onClick={() => setDetayTip(tip)}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }} onClick={e => e.stopPropagation()}>
                                        <IkonSecici value={tip.ikon} onChange={ik => handleTipIkon(tip, ik)} />
                                        <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{tip.tip}</span>
                                    </div>
                                    <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{tip.bolumler.length} bölüm</div>
                                    <div style={{ fontSize: 11.5, color: '#cd6906', fontWeight: 600, marginTop: 4 }}>Bölümleri Yönet →</div>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'vaziyet':
                return (
                    <div>
                        <h3 style={psH3}>Vaziyet Planı</h3>
                        <GaleriYonetici projeID={projeID} kategori="VaziyetPlani" ogeler={veri.vaziyetPlaniGorselleri}
                            onChange={g => setVeri({ ...veri, vaziyetPlaniGorselleri: g })} />
                    </div>
                );

            case 'video':
                return (
                    <div>
                        <h3 style={psH3}>Video</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                            {veri.videolar.map(v => (
                                <div key={v.projeVideoID} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 8 }}>
                                    <i className="fab fa-youtube" style={{ color: '#dc2626', fontSize: 16 }}></i>
                                    <span style={{ flex: 1, fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.baslik || v.youtubeLink}</span>
                                    <button type="button" onClick={() => handleVideoSil(v.projeVideoID)} style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', padding: 4 }}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ))}
                            {veri.videolar.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Henüz video eklenmedi.</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input type="text" placeholder="Başlık (opsiyonel)" value={videoBaslik} onChange={e => setVideoBaslik(e.target.value)} style={{ ...psInput, width: 160 }} />
                            <input type="text" placeholder="YouTube linki" value={videoLink} onChange={e => setVideoLink(e.target.value)} style={{ ...psInput, flex: 1 }} />
                            <button type="button" onClick={handleVideoEkle} disabled={videoEkleniyor} style={psBtnPrimary}>Ekle</button>
                        </div>
                    </div>
                );

            case 'disMekan':
            case 'konumCevre':
            case 'icMekan':
            case 'sosyalAlan': {
                const kategoriMap: Record<string, { key: keyof ProjeSayfaVeri; kategori: string; baslik: string }> = {
                    disMekan: { key: 'disMekan', kategori: 'DisMekan', baslik: 'Dış Mekan' },
                    konumCevre: { key: 'konumCevre', kategori: 'KonumCevre', baslik: 'Konum / Çevre' },
                    icMekan: { key: 'icMekan', kategori: 'IcMekan', baslik: 'İç Mekan' },
                    sosyalAlan: { key: 'sosyalAlan', kategori: 'SosyalAlan', baslik: 'Sosyal Alanlar' },
                };
                const conf = kategoriMap[aktifBolum];
                const bolumVeri = veri[conf.key] as { gorseller: GaleriOgesi[]; maddeler: MaddeOgesi[] };
                return (
                    <div>
                        <h3 style={psH3}>{conf.baslik}</h3>
                        <div style={{ marginBottom: 24 }}>
                            <div style={psAltBaslik}>Görseller</div>
                            <GaleriYonetici projeID={projeID} kategori={conf.kategori} ogeler={bolumVeri.gorseller}
                                onChange={g => setVeri({ ...veri, [conf.key]: { ...bolumVeri, gorseller: g } })} />
                        </div>
                        <div>
                            <div style={psAltBaslik}>Özellik Maddeleri</div>
                            <MaddeYonetici projeID={projeID} kategori={conf.kategori} ogeler={bolumVeri.maddeler}
                                onChange={m => setVeri({ ...veri, [conf.key]: { ...bolumVeri, maddeler: m } })} />
                        </div>
                    </div>
                );
            }
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1200, height: '90vh', display: 'flex', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
                {/* Sol menü */}
                <div style={{ width: 240, flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '18px 18px 14px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Proje Sayfası</div>
                        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{veri.projeAdi}</div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                        {BOLUMLER.map(b => (
                            <button key={b.key} type="button" data-testid={`ps-nav-${b.key}`} onClick={() => setAktifBolum(b.key)}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 12px', borderRadius: 9, border: 'none', textAlign: 'left',
                                    background: aktifBolum === b.key ? '#fff' : 'transparent',
                                    color: aktifBolum === b.key ? '#cd6906' : '#475569',
                                    boxShadow: aktifBolum === b.key ? '0 1px 4px rgba(15,23,42,0.08)' : 'none',
                                    fontWeight: aktifBolum === b.key ? 700 : 500,
                                    fontSize: 13, cursor: 'pointer', marginBottom: 2,
                                }}>
                                <i className={b.ikon} style={{ width: 16, textAlign: 'center' }}></i>
                                {b.ad}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sağ içerik */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer' }}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 28 }}>
                        {bolumIcerik()}
                    </div>
                </div>
            </div>

            {/* Daire Tipi Detay Modalı */}
            {detayTip && (
                <DaireTipiDetayModal
                    projeID={projeID}
                    tip={detayTip}
                    onClose={() => setDetayTip(null)}
                    onUpdate={updated => {
                        setVeri(v => v ? { ...v, daireTipleri: v.daireTipleri.map(t => t.daireTipID === updated.daireTipID ? updated : t) } : v);
                        setDetayTip(updated);
                    }}
                />
            )}
        </div>
    );
};

// ── Daire Tipi Detay Modalı: bölüm ekle / düzenle / sırala; her bölümde açıklama + birden fazla görsel ──
interface BolumForm { bolumAdi: string; aciklama: string; ikon: string | null; gorseller: string[]; }
const BOS_FORM: BolumForm = { bolumAdi: '', aciklama: '', ikon: null, gorseller: [] };

const DaireTipiDetayModal: React.FC<{ projeID: number; tip: DaireTipiSatir; onClose: () => void; onUpdate: (tip: DaireTipiSatir) => void }> = ({ projeID, tip, onClose, onUpdate }) => {
    const [form, setForm] = useState<BolumForm>(BOS_FORM);
    const [duzenlenenID, setDuzenlenenID] = useState<number | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [siralaniyor, setSiralaniyor] = useState(false);

    const handleGorseller = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setYukleniyor(true);
        try {
            const yuklenen: string[] = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('aciklama', 'Daire Tipi Bölüm Görseli');
                const up = await axiosInstance.post('/api/Dosyalar/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                yuklenen.push(up.data.dosyaAdi);
            }
            setForm(f => ({ ...f, gorseller: [...f.gorseller, ...yuklenen] }));
        } catch {
            toast.error('Görsel yüklenirken hata oluştu.');
        } finally {
            setYukleniyor(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const gorselTasi = (idx: number, yon: -1 | 1) => setForm(f => {
        const hedef = idx + yon;
        if (hedef < 0 || hedef >= f.gorseller.length) return f;
        const g = [...f.gorseller];
        [g[idx], g[hedef]] = [g[hedef], g[idx]];
        return { ...f, gorseller: g };
    });

    const gorselKaldir = (idx: number) => setForm(f => ({ ...f, gorseller: f.gorseller.filter((_, i) => i !== idx) }));

    const duzenlemeyeBasla = (b: DaireTipiBolum) => {
        setDuzenlenenID(b.projeDaireTipiBolumID);
        setForm({ bolumAdi: b.bolumAdi, aciklama: b.aciklama || '', ikon: b.ikon || null, gorseller: b.gorseller.map(g => g.gorselYolu) });
    };

    const formuSifirla = () => { setDuzenlenenID(null); setForm(BOS_FORM); };

    const handleKaydet = async () => {
        if (!form.bolumAdi.trim()) return;
        setKaydediliyor(true);
        const govde = { bolumAdi: form.bolumAdi.trim(), aciklama: form.aciklama.trim() || null, ikon: form.ikon, gorseller: form.gorseller };
        try {
            if (duzenlenenID !== null) {
                const res = await axiosInstance.post(`/api/ProjeSayfa/daire-tipi/bolum/update/${duzenlenenID}`, govde);
                onUpdate({ ...tip, bolumler: tip.bolumler.map(b => b.projeDaireTipiBolumID === duzenlenenID ? res.data : b) });
            } else {
                const res = await axiosInstance.post('/api/ProjeSayfa/daire-tipi/bolum', { projeID, daireTipID: tip.daireTipID, ...govde });
                onUpdate({ ...tip, projeDaireTipiID: tip.projeDaireTipiID ?? res.data.projeDaireTipiID ?? null, bolumler: [...tip.bolumler, res.data] });
            }
            formuSifirla();
        } catch {
            toast.error('Bölüm kaydedilirken hata oluştu.');
        } finally {
            setKaydediliyor(false);
        }
    };

    const handleSil = async (id: number) => {
        if (!window.confirm('Bu bölüm silinsin mi?')) return;
        try {
            await axiosInstance.post(`/api/ProjeSayfa/daire-tipi/bolum/delete/${id}`);
            onUpdate({ ...tip, bolumler: tip.bolumler.filter(b => b.projeDaireTipiBolumID !== id) });
            if (duzenlenenID === id) formuSifirla();
        } catch {
            toast.error('Silinirken hata oluştu.');
        }
    };

    const handleSirala = async (idx: number, yon: -1 | 1) => {
        const hedef = idx + yon;
        if (hedef < 0 || hedef >= tip.bolumler.length || tip.projeDaireTipiID == null) return;
        const yeni = [...tip.bolumler];
        [yeni[idx], yeni[hedef]] = [yeni[hedef], yeni[idx]];
        const onceki = tip;
        onUpdate({ ...tip, bolumler: yeni.map((b, i) => ({ ...b, sira: i + 1 })) });
        setSiralaniyor(true);
        try {
            await axiosInstance.post(`/api/ProjeSayfa/daire-tipi/${tip.projeDaireTipiID}/bolum-sirala`, { bolumIDler: yeni.map(b => b.projeDaireTipiBolumID) });
        } catch {
            toast.error('Sıralama kaydedilemedi.');
            onUpdate(onceki);
        } finally {
            setSiralaniyor(false);
        }
    };

    const kucukBtn: React.CSSProperties = { width: 26, height: 26, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 820, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{tip.tip} — Bölümler</div>
                    <button type="button" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#f1f5f9', color: '#64748b', cursor: 'pointer' }}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                <div style={{ padding: 22, overflowY: 'auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                        {tip.bolumler.map((b, idx) => {
                            const duzenleniyor = duzenlenenID === b.projeDaireTipiBolumID;
                            return (
                                <div key={b.projeDaireTipiBolumID} style={{ display: 'flex', gap: 12, padding: 10, background: duzenleniyor ? '#fff7ed' : '#f8fafc', border: `1px solid ${duzenleniyor ? '#fed7aa' : '#f1f5f9'}`, borderRadius: 10 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <button type="button" title="Yukarı taşı" onClick={() => handleSirala(idx, -1)} disabled={idx === 0 || siralaniyor} style={{ ...kucukBtn, opacity: idx === 0 ? 0.4 : 1 }}>
                                            <i className="fas fa-chevron-up"></i>
                                        </button>
                                        <button type="button" title="Aşağı taşı" onClick={() => handleSirala(idx, 1)} disabled={idx === tip.bolumler.length - 1 || siralaniyor} style={{ ...kucukBtn, opacity: idx === tip.bolumler.length - 1 ? 0.4 : 1 }}>
                                            <i className="fas fa-chevron-down"></i>
                                        </button>
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                                            <i className={b.ikon || 'fas fa-layer-group'} style={{ color: '#cd6906' }}></i>{b.bolumAdi}
                                            <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>· {b.gorseller.length} görsel</span>
                                        </div>
                                        {b.aciklama && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'pre-wrap' }}>{b.aciklama}</div>}
                                        {b.gorseller.length > 0 && (
                                            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                                {b.gorseller.map(g => (
                                                    <img key={g.projeDaireTipiBolumGorselID} src={`${API_BASE_URL}/userFiles/${g.gorselYolu}`} alt="" style={{ width: 52, height: 52, borderRadius: 6, objectFit: 'cover' }} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, alignSelf: 'flex-start' }}>
                                        <button type="button" title="Düzenle" onClick={() => duzenlemeyeBasla(b)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4 }}>
                                            <i className="fas fa-pen"></i>
                                        </button>
                                        <button type="button" title="Sil" onClick={() => handleSil(b.projeDaireTipiBolumID)} style={{ background: 'none', border: 'none', color: '#f43f5e', cursor: 'pointer', padding: 4 }}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {tip.bolumler.length === 0 && <div style={{ fontSize: 12.5, color: '#94a3b8' }}>Henüz bölüm eklenmedi.</div>}
                    </div>

                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {duzenlenenID !== null ? 'Bölümü Düzenle' : 'Bölüm Ekle'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <IkonSecici value={form.ikon} onChange={ik => setForm(f => ({ ...f, ikon: ik }))} />
                            <input type="text" placeholder="Bölüm Adı (örn: Salon)" value={form.bolumAdi} maxLength={100} onChange={e => setForm(f => ({ ...f, bolumAdi: e.target.value }))} style={{ ...psInput, flex: 1 }} />
                        </div>
                        <textarea placeholder="Bölüm açıklaması..." value={form.aciklama} maxLength={1000} onChange={e => setForm(f => ({ ...f, aciklama: e.target.value }))} rows={3}
                            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }} />
                        {form.gorseller.length > 0 && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                                {form.gorseller.map((g, idx) => (
                                    <div key={`${g}-${idx}`} style={{ position: 'relative', width: 96, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <img src={`${API_BASE_URL}/userFiles/${g}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        <span style={{ position: 'absolute', top: 3, left: 3, background: 'rgba(15,23,42,0.7)', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px' }}>{idx + 1}</span>
                                        <button type="button" title="Kaldır" onClick={() => gorselKaldir(idx)} style={{ position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: '50%', border: 'none', background: 'rgba(244,63,94,0.9)', color: '#fff', cursor: 'pointer', fontSize: 10, padding: 0 }}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                        <div style={{ position: 'absolute', bottom: 3, right: 3, display: 'flex', gap: 3 }}>
                                            {idx > 0 && <button type="button" title="Sola taşı" onClick={() => gorselTasi(idx, -1)} style={{ ...kucukBtn, width: 20, height: 20, fontSize: 9 }}><i className="fas fa-chevron-left"></i></button>}
                                            {idx < form.gorseller.length - 1 && <button type="button" title="Sağa taşı" onClick={() => gorselTasi(idx, 1)} style={{ ...kucukBtn, width: 20, height: 20, fontSize: 9 }}><i className="fas fa-chevron-right"></i></button>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleGorseller} style={{ display: 'none' }} />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={yukleniyor} style={psBtnGhost}>
                                {yukleniyor
                                    ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Yükleniyor...</>
                                    : <><i className="fas fa-images" style={{ marginRight: 6 }}></i>Görsel Ekle</>}
                            </button>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                {duzenlenenID !== null && (
                                    <button type="button" onClick={formuSifirla} style={psBtnGhost}>Vazgeç</button>
                                )}
                                <button type="button" onClick={handleKaydet} disabled={kaydediliyor || yukleniyor || !form.bolumAdi.trim()} style={psBtnPrimary}>
                                    {kaydediliyor ? 'Kaydediliyor...' : (duzenlenenID !== null ? 'Kaydet' : 'Bölüm Ekle')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const psH3: React.CSSProperties = { fontSize: 17, fontWeight: 700, color: '#0f172a', marginTop: 0, marginBottom: 16 };
const psAltBaslik: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' };
const psAciklama: React.CSSProperties = { fontSize: 12.5, color: '#94a3b8', marginTop: -8, marginBottom: 16 };
const psInput: React.CSSProperties = { padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit' };
const psBtnPrimary: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };
const psBtnGhost: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' };

export default ProjeSayfaYonetim;
